import {Injectable} from '@angular/core';
import {HttpParams} from '@angular/common/http';
import {AngularFirestore} from '@angular/fire/compat/firestore';
import {Observable, forkJoin} from 'rxjs';
import {map, take} from 'rxjs/operators';
import {SickNoteBody} from '../models/sick-note';
import {Send} from '../pages/chat/shared/models/chat.model';
import {ProfessionalResponse, VaccineTypeResponse, ListICDsResponse} from '../models/responses';
import {ApiService, API_SEARCH_ICDS, API_PROFESSIONALS} from './api.service';

@Injectable({
  providedIn: 'root'
})

export class ChatService {
  public allowanceHours: number;

  private $data: SickNoteBody = {};
  private $invalidICDs: Array<string>;

  constructor(
    private db: AngularFirestore,
    private api: ApiService
  ) {}

  public get data(): SickNoteBody {
    return this.$data;
  }

  public get invalidICDs(): Array<string> {
    return this.$invalidICDs;
  }

  public resetData(): void {
    this.$data = {};
  }

  public removeData(keys: Array<string> = []): void {
    keys.forEach((key: string) => delete this.$data[key]);
  }

  public buildData(sends: Send | Array<Send>): void {
    if (!Array.isArray(sends)) {
      return this.addData(sends);
    }

    sends.forEach((send: Send) => this.addData(send));
  }

  public validateICDs(): Observable<boolean> {
    const requests: Array<Observable<ListICDsResponse>> = [];
    const icds: Array<string> = this.$data.icds.split(', ');

    let params: HttpParams = new HttpParams();
    params = params.append('obj_per_page', '99');

    for (const icd of icds) {
      requests.push(this.api.http.get<ListICDsResponse>(API_SEARCH_ICDS, {
        params: params.append('query', icd)
      }));
    }

    return forkJoin(requests)
      .pipe(map((results: Array<ListICDsResponse>) => this.checkICDs(results, icds)));
  }

  public getProfessionals(): Observable<string> {
    return this.api.http.get(API_PROFESSIONALS(this.data.professional_id, this.data.professional_state))
      .pipe(map((professional: ProfessionalResponse) => professional.name));
  }

  public getVaccineTypes(): Observable<{[key: string]: string}> {
    return this.db
      .collection<VaccineTypeResponse>('vaccines', ref => ref.orderBy('order'))
      .valueChanges({idField: 'id'})
      .pipe(
        take(1),
        map((listVaccines: Array<VaccineTypeResponse>) => {
          const vaccines: {[key: string]: string} = {};

          listVaccines.forEach((vaccineType: VaccineTypeResponse) => vaccines[vaccineType.id] = vaccineType.name);

          return vaccines;
        })
      );
  }

  private addData(send: Send): void {
    if (send.reference) {
      this.$data[send.reference] = send.value;
    }
  }

  private checkICDs(results: Array<ListICDsResponse>, icds: Array<string>): boolean {
    this.$invalidICDs = [];

    results.forEach((result: ListICDsResponse, i: number) => {
      if (!result.icds.length) {
        this.$invalidICDs.push(icds[i]);
      }
    });

    return !this.invalidICDs.length;
  }
}
